import classNames from 'classnames'
import merge from 'lodash.merge'
import React from 'react'

import { ObjectHash } from '../../../types/shared'
import { SpecificChartFunctionality } from '../../constants/common'
import { useModelState } from '../../model-hooks/use-model-state'
import Quote from '../../models/quote'
import { getChartsDimensions, isRedesignEnabled } from '../../utils'
import { prefetchPerfIndicatorAllQuotes } from '../../utils/chart'
import { getLeftOffset } from '../renderUtils'
import { ChartSpinnerOverlay } from './chart-spinner-overlay'
import { CHART_CLASS_NAMES, ChartProps, DEFAULT_WRAPPER_COMPONENT } from './constants'

export function withChartInit(Component: React.ComponentType<ChartProps>) {
  return function WrappedComponent(props: ChartProps) {
    const { WrapperComp = DEFAULT_WRAPPER_COMPONENT, gridArea } = props
    const isModelDestroyedRef = React.useRef(false)
    const model = useModelState(props.chartModel, { watchProperties: ['panes', 'quote'] })
    const layoutModel = useModelState(props.layoutModel, { watchProperties: ['activeChart'] })
    const quote = model.quote()

    const initChartRef = React.useRef(async () => {
      const { chartModel, config } = props
      const { premarket, hasChartEvents, aftermarket, ticker, instrument, timeframe, panes: panesConfig } = config

      const options = {
        premarket,
        aftermarket,
        events: hasChartEvents,
        patterns: panesConfig.some(
          (pane) =>
            pane.elements?.some(
              (element) => element.overlays?.some((overlay: ObjectHash) => overlay.type === 'overlays/patterns')
            )
        ),
      }

      const quote = await Quote.get({
        ticker,
        instrument,
        timeframe,
        options,
      })
      const panesWithPerfIndicator = panesConfig.filter(({ elements }) =>
        elements.some(({ type }) => type === 'indicators/perf')
      )

      if (panesWithPerfIndicator) {
        await Promise.allSettled(
          panesWithPerfIndicator.map((paneWithPerfIndicator) =>
            prefetchPerfIndicatorAllQuotes({ paneWithPerfIndicator, timeframe, options })
          )
        )
      }

      if (isModelDestroyedRef.current) {
        return
      }

      config.panes.forEach((pane) => {
        const paneProperties = merge({}, pane)
        paneProperties.chart = chartModel
        chartModel.createPaneCascade(paneProperties)
      })

      chartModel.updateAttributes({
        quote,
        premarket: options.premarket,
        aftermarket: options.aftermarket,
      })

      chartModel.setChartEvents(quote.chartEvents, true)

      if (props.shouldResize) {
        const chartsDimensions = getChartsDimensions(props.layoutModel.getHTMLElementId())
        props.layoutModel.updateAttributes(chartsDimensions)
      }

      if (chartModel.leftOffset == null) {
        const leftOffset = getLeftOffset({ quote, chartModel: chartModel, config: props.config })
        chartModel.updateAttribute('leftOffset', leftOffset)
      }
    })

    React.useEffect(() => {
      const handleModelBeforeDestroy = () => {
        isModelDestroyedRef.current = true
      }

      model.bind('beforeDestroy', handleModelBeforeDestroy)
      initChartRef.current()
      return () => {
        model.unbind('beforeDestroy', handleModelBeforeDestroy)
      }
      // We want to run it only on mount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isQuotePage = props.layoutModel.specificChartFunctionality === SpecificChartFunctionality.quotePage
    const isChartPage = props.layoutModel.specificChartFunctionality === SpecificChartFunctionality.chartPage
    const isFuturesForexCryptoPage = [
      SpecificChartFunctionality.futuresPage,
      SpecificChartFunctionality.forexPage,
      SpecificChartFunctionality.cryptoPage,
    ].includes(props.layoutModel.specificChartFunctionality)
    const isModelReady =
      quote && ((quote.fetchedAt === undefined && quote.close.length > 0) || quote.fetchedAt > 0 || quote)
    const loadingStyle =
      !isModelReady && isQuotePage
        ? // 2px is border
          { width: model.width ? model.width + 2 : '100%', height: model.height + 2 }
        : undefined

    return (
      <WrapperComp
        className={classNames(CHART_CLASS_NAMES, {
          active: isQuotePage,
          'overflow-hidden': isFuturesForexCryptoPage && isRedesignEnabled(),
          'outline outline-1 outline-blue-400': layoutModel.activeChart.eql(model) && props.hasOutline,
        })}
        {...(WrapperComp !== DEFAULT_WRAPPER_COMPONENT ? { gridArea } : {})}
        data-testid={`chart-${props.chartIndex}-container`}
        style={loadingStyle}
      >
        {isChartPage && (
          <ChartSpinnerOverlay
            className={classNames({
              'bg-white': isQuotePage,
              'bg-gray-50 dark:bg-gray-800': isChartPage,
            })}
            shouldDisplay={!isModelReady}
          />
        )}
        {isModelReady && <Component {...props} />}
      </WrapperComp>
    )
  }
}
